import {
  CloseOutlined,
  DeleteOutlined,
  DragOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  AutoComplete,
  Button,
  Input,
  Select,
  Tag,
} from '@surecompanies/core_components'
import React, { memo, useCallback, useMemo, useRef } from 'react'
import styles from '../CreateCensusTemplate.module.scss'
import {
  ALL_CONDITION_TYPES,
  ALL_NORMALIZE_TYPES,
  ALL_NUMERIC_COMPARISON_OPERATORS,
  CONCAT_MAX_SOURCES,
  CONCAT_MIN_SOURCES,
  CONDITION_LABELS,
  ConditionType,
  NORMALIZE_LABELS,
  NUMERIC_COMPARISON_OPERATOR_LABELS,
  NUMERIC_COMPARISON_OPERATOR_SYMBOLS,
  NumericComparisonOperator,
  NormalizeType,
  type ConcatRule,
  type MappingRule,
  type NormalizeStep,
  type SourceEntry,
} from '../types'
import {
  buildNestedMapping,
  flattenMultiMapping,
  type MultiSourceMappingLeaf,
} from '../utils/lookupMapping'
import {
  evaluateConcat,
  parseNumericInput,
  validateConcatRule,
} from '../utils/transformer'

export interface ColumnCardProps {
  rule: MappingRule
  index: number
  sourceColumns: string[]
  /** Names of computed_fields targets, offered as sources grouped apart from raw columns. */
  computedFieldNames: string[]
  sourceValuesByColumn?: Record<string, string[]>
  onChange: (updated: MappingRule) => void
  onRemove: (id: string) => void
  /** When set, shows a drag handle (sortable mode). Omit in read-only view. */
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>
  isMoved?: boolean
  readOnly?: boolean
}

interface MappingEntry {
  _id: string
  key: string
  value: string
}

const searchableSelectProps = {
  showSearch: true,
  optionFilterProp: 'label' as const,
}

interface SourceOption {
  label: string
  value: string
}

interface SourceOptionGroup {
  label: string
  options: SourceOption[]
}

/**
 * Groups computed-field names apart from raw source columns so authors can
 * tell them apart in a source-picker dropdown. Falls back to a flat list
 * when there are no computed fields yet, to avoid a group header for a
 * group that doesn't exist.
 *
 * Returns a single array whose elements may individually be a leaf option or
 * a group (rather than a union of two whole-array types) so antd's Select
 * can infer its option type from the prop as it does for the plain flat
 * list case.
 */
function buildSourceOptions(
  sourceColumns: string[],
  computedFieldNames: string[]
): (SourceOption | SourceOptionGroup)[] {
  const sourceOptions = sourceColumns.map((c) => ({ label: c, value: c }))
  if (computedFieldNames.length === 0) {
    return sourceOptions
  }
  return [
    { label: 'Source Columns', options: sourceOptions },
    {
      label: 'Computed Fields',
      options: computedFieldNames.map((c) => ({
        label: `Computed: ${c}`,
        value: c,
      })),
    },
  ]
}

let entryIdCounter = 0
function nextEntryId() {
  entryIdCounter += 1
  return `entry-${entryIdCounter}`
}

function mappingToEntries(
  mapping: Record<string, unknown> | undefined
): MappingEntry[] {
  if (!mapping) return []
  return Object.entries(mapping).map(([k, v]) => ({
    _id: nextEntryId(),
    key: k,
    value: String(v ?? ''),
  }))
}

function entriesToMapping(entries: MappingEntry[]): Record<string, string> {
  const result: Record<string, string> = {}
  for (const e of entries) {
    result[e.key] = e.value
  }
  return result
}

interface MultiSourceMappingEntry extends MultiSourceMappingLeaf {
  _id: string
}

function leavesToEntries(
  leaves: MultiSourceMappingLeaf[]
): MultiSourceMappingEntry[] {
  return leaves.map((l) => ({ _id: nextEntryId(), ...l }))
}

const NormalizationEditor = memo(function NormalizationEditor({
  steps,
  onChange,
  readOnly,
}: {
  steps: NormalizeStep[]
  onChange: (steps: NormalizeStep[]) => void
  readOnly?: boolean
}) {
  const handleAddStep = useCallback(() => {
    onChange([...steps, { type: NormalizeType.TRIM }])
  }, [steps, onChange])

  const handleRemoveStep = useCallback(
    (index: number) => {
      onChange(steps.filter((_, i) => i !== index))
    },
    [steps, onChange]
  )

  const handleStepTypeChange = useCallback(
    (index: number, type: NormalizeType) => {
      const updated = [...steps]
      let step: NormalizeStep

      switch (type) {
        case NormalizeType.REMOVE_SPECIAL_CHARS:
          step = { type, chars: ['-', ' '] }
          break
        case NormalizeType.PAD_LEFT:
          step = { type, length: 9, pad_char: '0' }
          break
        case NormalizeType.PAD_RIGHT:
          step = { type, length: 9, pad_char: '0' }
          break
        case NormalizeType.DATE_FORMAT:
          step = { type, format: 'MM/DD/YYYY' }
          break
        case NormalizeType.MAX_LENGTH:
          step = { type, length: 255 }
          break
        case NormalizeType.SUBSTRING:
          step = { type, start: 0, length: 5 }
          break
        case NormalizeType.REPLACE:
          step = { type, pattern: '', replacement: '', case_insensitive: false }
          break
        case NormalizeType.SLICE_LEFT:
        case NormalizeType.SLICE_RIGHT:
          step = { type, length: 5 }
          break
        default:
          step = { type } as NormalizeStep
          break
      }

      updated[index] = step
      onChange(updated)
    },
    [steps, onChange]
  )

  const handleStepFieldChange = useCallback(
    (index: number, field: string, value: unknown) => {
      const updated = [...steps]
      updated[index] = { ...updated[index], [field]: value }
      onChange(updated)
    },
    [steps, onChange]
  )

  return (
    <div className={styles.normalizeSection}>
      <div className={styles.normalizeSectionHeader}>
        <span className={styles.normalizeSectionTitle}>NORMALIZATION</span>
        {!readOnly && (
          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddStep}
          >
            Add Step
          </Button>
        )}
      </div>

      {steps.map((step, i) => (
        <div key={i} className={styles.normalizeStep}>
          <div className={styles.normalizeStepHeader}>
            <Tag color="#003146" style={{ fontSize: 11 }}>
              Step {i + 1}
            </Tag>
            {!readOnly && (
              <DeleteOutlined
                className={styles.normalizeStepRemove}
                onClick={() => handleRemoveStep(i)}
              />
            )}
          </div>

          <Select
            size="small"
            value={step.type}
            onChange={(val: NormalizeType) => handleStepTypeChange(i, val)}
            style={{ width: '100%', marginBottom: 6 }}
            disabled={readOnly}
            {...searchableSelectProps}
            options={ALL_NORMALIZE_TYPES.map((t) => ({
              label: NORMALIZE_LABELS[t],
              value: t,
            }))}
          />

          {step.type === NormalizeType.REMOVE_SPECIAL_CHARS && (
            <Input
              size="small"
              placeholder='Characters (e.g. -, " ")'
              value={step.chars?.join(', ') ?? ''}
              disabled={readOnly}
              onChange={(e) =>
                handleStepFieldChange(
                  i,
                  'chars',
                  e.target.value.split(',').map((c) => c.trim())
                )
              }
            />
          )}

          {(step.type === NormalizeType.PAD_LEFT ||
            step.type === NormalizeType.PAD_RIGHT) && (
            <div className={styles.normalizeStepRow}>
              <Input
                size="small"
                type="number"
                placeholder="Length"
                value={step.length}
                disabled={readOnly}
                onChange={(e) =>
                  handleStepFieldChange(i, 'length', Number(e.target.value))
                }
                style={{ flex: 1 }}
              />
              <Input
                size="small"
                placeholder="Pad char"
                value={step.pad_char ?? ''}
                disabled={readOnly}
                onChange={(e) =>
                  handleStepFieldChange(i, 'pad_char', e.target.value)
                }
                style={{ flex: 1 }}
              />
            </div>
          )}

          {step.type === NormalizeType.DATE_FORMAT && (
            <Input
              size="small"
              placeholder="Format (e.g. MM/DD/YYYY)"
              value={step.format ?? ''}
              disabled={readOnly}
              onChange={(e) =>
                handleStepFieldChange(i, 'format', e.target.value)
              }
            />
          )}

          {step.type === NormalizeType.MAX_LENGTH && (
            <Input
              size="small"
              type="number"
              placeholder="Max length"
              value={step.length}
              disabled={readOnly}
              onChange={(e) =>
                handleStepFieldChange(i, 'length', Number(e.target.value))
              }
            />
          )}

          {(step.type === NormalizeType.SLICE_LEFT ||
            step.type === NormalizeType.SLICE_RIGHT) && (
            <Input
              size="small"
              type="number"
              placeholder="Length"
              value={step.length}
              disabled={readOnly}
              onChange={(e) =>
                handleStepFieldChange(i, 'length', Number(e.target.value))
              }
            />
          )}

          {step.type === NormalizeType.SUBSTRING && (
            <div className={styles.normalizeStepRow}>
              <Input
                size="small"
                type="number"
                placeholder="Start"
                value={step.start}
                disabled={readOnly}
                onChange={(e) =>
                  handleStepFieldChange(i, 'start', Number(e.target.value))
                }
                style={{ flex: 1 }}
              />
              <Input
                size="small"
                type="number"
                placeholder="Length"
                value={step.length}
                disabled={readOnly}
                onChange={(e) =>
                  handleStepFieldChange(i, 'length', Number(e.target.value))
                }
                style={{ flex: 1 }}
              />
            </div>
          )}

          {step.type === NormalizeType.REPLACE && (
            <>
              <div className={styles.normalizeStepRow}>
                <Input
                  size="small"
                  placeholder="Pattern"
                  value={step.pattern ?? ''}
                  disabled={readOnly}
                  onChange={(e) =>
                    handleStepFieldChange(i, 'pattern', e.target.value)
                  }
                  style={{ flex: 1 }}
                />
                <Input
                  size="small"
                  placeholder="Replacement"
                  value={step.replacement ?? ''}
                  disabled={readOnly}
                  onChange={(e) =>
                    handleStepFieldChange(i, 'replacement', e.target.value)
                  }
                  style={{ flex: 1 }}
                />
              </div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  marginTop: 4,
                }}
              >
                <input
                  type="checkbox"
                  checked={step.case_insensitive ?? false}
                  disabled={readOnly}
                  onChange={(e) =>
                    handleStepFieldChange(
                      i,
                      'case_insensitive',
                      e.target.checked
                    )
                  }
                />
                Case insensitive
              </label>
            </>
          )}
        </div>
      ))}

      {steps.length === 0 && (
        <div className={styles.normalizeEmpty}>
          No normalization steps. Click &quot;Add Step&quot; to begin.
        </div>
      )}
    </div>
  )
})

const SingleSourceMappingTable = memo(function SingleSourceMappingTable({
  rule,
  sourceValuesByColumn,
  onChange,
  readOnly,
}: {
  rule: MappingRule
  sourceValuesByColumn?: Record<string, string[]>
  onChange: (updated: MappingRule) => void
  readOnly?: boolean
}) {
  const sourceValueOptions = useMemo(() => {
    if (!sourceValuesByColumn || !rule.source) return []
    const values = sourceValuesByColumn[rule.source] ?? []
    return [...values]
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ label: v, value: v }))
  }, [sourceValuesByColumn, rule.source])

  const entriesRef = useRef<MappingEntry[]>(mappingToEntries(rule.mapping))
  const lastSyncedMappingRef = useRef(rule.mapping)

  if (rule.mapping !== lastSyncedMappingRef.current) {
    const incomingJson = JSON.stringify(rule.mapping ?? {})
    const currentJson = JSON.stringify(entriesToMapping(entriesRef.current))
    if (incomingJson !== currentJson) {
      entriesRef.current = mappingToEntries(rule.mapping)
    }
    lastSyncedMappingRef.current = rule.mapping
  }

  const syncEntriesToRule = useCallback(
    (entries: MappingEntry[]) => {
      entriesRef.current = entries
      const newMapping = entriesToMapping(entries)
      lastSyncedMappingRef.current = newMapping
      onChange({ ...rule, mapping: newMapping })
    },
    [rule, onChange]
  )

  const handleAddEntry = useCallback(() => {
    syncEntriesToRule([
      ...entriesRef.current,
      { _id: nextEntryId(), key: '', value: '' },
    ])
  }, [syncEntriesToRule])

  const handleRemoveEntry = useCallback(
    (id: string) => {
      syncEntriesToRule(entriesRef.current.filter((e) => e._id !== id))
    },
    [syncEntriesToRule]
  )

  const handleEntryKeyChange = useCallback(
    (id: string, newKey: string) => {
      syncEntriesToRule(
        entriesRef.current.map((e) =>
          e._id === id ? { ...e, key: newKey } : e
        )
      )
    },
    [syncEntriesToRule]
  )

  const handleEntryValueChange = useCallback(
    (id: string, newValue: string) => {
      syncEntriesToRule(
        entriesRef.current.map((e) =>
          e._id === id ? { ...e, value: newValue } : e
        )
      )
    },
    [syncEntriesToRule]
  )

  return (
    <div className={styles.columnCardField}>
      <div className={styles.mappingHeader}>
        <span className={styles.columnCardFieldLabel}>MAPPING TABLE</span>
        {!readOnly && (
          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddEntry}
          >
            Add
          </Button>
        )}
      </div>
      {entriesRef.current.map((entry) => (
        <div key={entry._id} className={styles.mappingRow}>
          <AutoComplete
            size="small"
            placeholder="Source value"
            value={entry.key}
            disabled={readOnly}
            onChange={(val: string) => handleEntryKeyChange(entry._id, val)}
            options={sourceValueOptions}
            style={{ flex: 1 }}
            filterOption={(input, option) =>
              String(option?.value ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
          <span className={styles.mappingArrow}>→</span>
          <Input
            size="small"
            placeholder="Target value"
            value={entry.value}
            disabled={readOnly}
            onChange={(e) => handleEntryValueChange(entry._id, e.target.value)}
            style={{ flex: 1 }}
          />
          {!readOnly && (
            <DeleteOutlined
              className={styles.mappingRowDelete}
              onClick={() => handleRemoveEntry(entry._id)}
            />
          )}
        </div>
      ))}
    </div>
  )
})

const MultiSourceMappingTable = memo(function MultiSourceMappingTable({
  rule,
  sources,
  sourceValuesByColumn,
  onChange,
  readOnly,
}: {
  rule: MappingRule
  sources: string[]
  sourceValuesByColumn?: Record<string, string[]>
  onChange: (updated: MappingRule) => void
  readOnly?: boolean
}) {
  const sourceCount = sources.length

  const optionsBySourceIndex = useMemo(() => {
    return sources.map((col) => {
      const baseValues = sourceValuesByColumn?.[col] ?? []
      const sorted = [...baseValues].sort((a, b) => a.localeCompare(b))
      return [
        {
          label: 'Any value (matches anything not listed)',
          value: '*',
        },
        ...sorted.map((v) => ({ label: v, value: v })),
      ]
    })
  }, [sources, sourceValuesByColumn])

  const entriesRef = useRef<MultiSourceMappingEntry[]>(
    leavesToEntries(flattenMultiMapping(rule.mapping, sourceCount))
  )
  const lastSyncedMappingRef = useRef(rule.mapping)
  const lastSyncedDepthRef = useRef(sourceCount)

  if (
    rule.mapping !== lastSyncedMappingRef.current ||
    sourceCount !== lastSyncedDepthRef.current
  ) {
    const incomingJson = JSON.stringify(rule.mapping ?? {})
    const currentJson = JSON.stringify(buildNestedMapping(entriesRef.current))
    if (
      incomingJson !== currentJson ||
      sourceCount !== lastSyncedDepthRef.current
    ) {
      entriesRef.current = leavesToEntries(
        flattenMultiMapping(rule.mapping, sourceCount)
      )
    }
    lastSyncedMappingRef.current = rule.mapping
    lastSyncedDepthRef.current = sourceCount
  }

  const syncEntriesToRule = useCallback(
    (entries: MultiSourceMappingEntry[]) => {
      entriesRef.current = entries
      const newMapping = buildNestedMapping(entries)
      lastSyncedMappingRef.current = newMapping
      lastSyncedDepthRef.current = sourceCount
      onChange({ ...rule, mapping: newMapping })
    },
    [rule, onChange, sourceCount]
  )

  const handleAddEntry = useCallback(() => {
    const blankKeys = Array.from({ length: sourceCount }, () => '')
    syncEntriesToRule([
      ...entriesRef.current,
      { _id: nextEntryId(), keys: blankKeys, value: '' },
    ])
  }, [syncEntriesToRule, sourceCount])

  const handleRemoveEntry = useCallback(
    (id: string) => {
      syncEntriesToRule(entriesRef.current.filter((e) => e._id !== id))
    },
    [syncEntriesToRule]
  )

  const handleEntryKeyChange = useCallback(
    (id: string, keyIndex: number, newKey: string) => {
      syncEntriesToRule(
        entriesRef.current.map((e) => {
          if (e._id !== id) return e
          const keys = [...e.keys]
          while (keys.length < sourceCount) keys.push('')
          keys[keyIndex] = newKey
          return { ...e, keys: keys.slice(0, sourceCount) }
        })
      )
    },
    [syncEntriesToRule, sourceCount]
  )

  const handleEntryValueChange = useCallback(
    (id: string, newValue: string) => {
      syncEntriesToRule(
        entriesRef.current.map((e) =>
          e._id === id ? { ...e, value: newValue } : e
        )
      )
    },
    [syncEntriesToRule]
  )

  return (
    <div className={styles.columnCardField}>
      <div className={styles.mappingHeader}>
        <span className={styles.columnCardFieldLabel}>MAPPING TABLE</span>
        {!readOnly && (
          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddEntry}
            disabled={sourceCount === 0}
          >
            Add
          </Button>
        )}
      </div>
      {sourceCount > 0 && entriesRef.current.length > 0 && (
        <>
          <div
            className={styles.mappingMultiHint}
            data-testid="lookup-mapping-hint"
          >
            Tip: pick <strong>Any value</strong> from the dropdown (stored as
            <code> *</code>) to match any value not explicitly listed.
          </div>
          <div
            className={styles.mappingMultiHeaderRow}
            data-testid="lookup-mapping-header"
          >
            {sources.map((s, i) => (
              <span
                key={i}
                className={styles.mappingMultiHeaderLabel}
                title={s || `Source ${i + 1}`}
                data-testid="lookup-mapping-header-source"
              >
                {s || `Source ${i + 1}`}
              </span>
            ))}
            <span className={styles.mappingMultiHeaderArrow} />
            <span className={styles.mappingMultiHeaderLabel}>Target value</span>
            {!readOnly && <span className={styles.mappingMultiHeaderDelete} />}
          </div>
        </>
      )}
      {entriesRef.current.map((entry) => {
        const isWildcardRow = entry.keys
          .slice(0, sourceCount)
          .some((k) => k === '*')
        return (
          <div
            key={entry._id}
            className={`${styles.mappingMultiRow} ${
              isWildcardRow ? styles.mappingMultiRowWildcard : ''
            }`}
            data-testid="lookup-mapping-row"
          >
            {Array.from({ length: sourceCount }, (_, i) => {
              const keyVal = entry.keys[i] ?? ''
              if (keyVal === '*') {
                return (
                  <div
                    key={i}
                    className={styles.mappingWildcardCell}
                    title="Matches any value of this source not explicitly listed in another row"
                    data-testid="lookup-mapping-wildcard-pill"
                  >
                    <span className={styles.mappingWildcardLabel}>
                      Any value
                    </span>
                    {!readOnly && (
                      <button
                        type="button"
                        className={styles.mappingWildcardClear}
                        onClick={() => handleEntryKeyChange(entry._id, i, '')}
                        aria-label="Replace wildcard with a specific value"
                      >
                        <CloseOutlined />
                      </button>
                    )}
                  </div>
                )
              }
              return (
                <AutoComplete
                  key={i}
                  size="small"
                  placeholder={sources[i] || `Source ${i + 1}`}
                  value={keyVal}
                  disabled={readOnly}
                  onChange={(val: string) =>
                    handleEntryKeyChange(entry._id, i, val)
                  }
                  options={optionsBySourceIndex[i] ?? []}
                  style={{ flex: 1, minWidth: 0 }}
                  filterOption={(input, option) =>
                    String(option?.value ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              )
            })}
            <span className={styles.mappingArrow}>→</span>
            <Input
              size="small"
              placeholder={
                isWildcardRow
                  ? 'Fallback value (leave empty for none)'
                  : 'Target value'
              }
              value={entry.value}
              disabled={readOnly}
              onChange={(e) =>
                handleEntryValueChange(entry._id, e.target.value)
              }
              style={{ flex: 1, minWidth: 0 }}
              data-testid="lookup-mapping-target"
            />
            {!readOnly && (
              <DeleteOutlined
                className={styles.mappingRowDelete}
                onClick={() => handleRemoveEntry(entry._id)}
                data-testid="lookup-mapping-row-delete"
              />
            )}
          </div>
        )
      })}
    </div>
  )
})

const LookupMapEditor = memo(function LookupMapEditor({
  rule,
  sourceColumns,
  computedFieldNames,
  sourceValuesByColumn,
  onChange,
  readOnly,
}: {
  rule: MappingRule
  sourceColumns: string[]
  computedFieldNames: string[]
  sourceValuesByColumn?: Record<string, string[]>
  onChange: (updated: MappingRule) => void
  readOnly?: boolean
}) {
  const isMultiSource = rule.sources !== undefined && rule.sources.length > 1

  return (
    <>
      {!isMultiSource && (
        <div className={styles.columnCardField}>
          <span className={styles.columnCardFieldLabel}>SOURCE</span>
          <Select
            size="small"
            value={rule.source ?? undefined}
            onChange={(val: string) => onChange({ ...rule, source: val })}
            style={{ width: '100%' }}
            placeholder="Select source column"
            disabled={readOnly}
            popupMatchSelectWidth={false}
            {...searchableSelectProps}
            options={buildSourceOptions(sourceColumns, computedFieldNames)}
          />
        </div>
      )}

      {isMultiSource && (
        <div className={styles.columnCardField}>
          <span className={styles.columnCardFieldLabel}>SOURCES</span>
          <Select
            mode="multiple"
            size="small"
            value={rule.sources ?? []}
            onChange={(val: string[]) => onChange({ ...rule, sources: val })}
            style={{ width: '100%' }}
            placeholder="Select source columns"
            disabled={readOnly}
            popupMatchSelectWidth={false}
            {...searchableSelectProps}
            options={buildSourceOptions(sourceColumns, computedFieldNames)}
          />
        </div>
      )}

      {isMultiSource ? (
        <MultiSourceMappingTable
          rule={rule}
          sources={rule.sources ?? []}
          sourceValuesByColumn={sourceValuesByColumn}
          onChange={onChange}
          readOnly={readOnly}
        />
      ) : (
        <SingleSourceMappingTable
          rule={rule}
          sourceValuesByColumn={sourceValuesByColumn}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}

      <div className={styles.columnCardField}>
        <span className={styles.columnCardFieldLabel}>DEFAULT VALUE</span>
        <Input
          size="small"
          placeholder="Fallback when no match"
          value={rule.default ?? ''}
          disabled={readOnly}
          onChange={(e) => onChange({ ...rule, default: e.target.value })}
        />
      </div>

      {!readOnly && (
        <div className={styles.toggleMultiSource}>
          <Button
            size="small"
            type="link"
            onClick={() => {
              if (isMultiSource) {
                onChange({
                  ...rule,
                  source: rule.sources?.[0] ?? '',
                  sources: undefined,
                  mapping: {},
                })
              } else {
                onChange({
                  ...rule,
                  sources: [rule.source ?? '', ''],
                  source: undefined,
                  mapping: {},
                })
              }
            }}
          >
            {isMultiSource
              ? 'Switch to single source'
              : 'Switch to multiple sources'}
          </Button>
        </div>
      )}
    </>
  )
})

const FirstAvailableMapEditor = memo(function FirstAvailableMapEditor({
  rule,
  sourceColumns,
  computedFieldNames,
  onChange,
  readOnly,
}: {
  rule: MappingRule
  sourceColumns: string[]
  computedFieldNames: string[]
  onChange: (updated: MappingRule) => void
  readOnly?: boolean
}) {
  const entries = rule.sourceEntries ?? []

  const handleAddEntry = useCallback(() => {
    onChange({
      ...rule,
      sourceEntries: [...entries, { source: '', value: '' }],
    })
  }, [rule, entries, onChange])

  const handleRemoveEntry = useCallback(
    (index: number) => {
      onChange({
        ...rule,
        sourceEntries: entries.filter((_, i) => i !== index),
      })
    },
    [rule, entries, onChange]
  )

  const handleEntryChange = useCallback(
    (index: number, field: keyof SourceEntry, val: string) => {
      const updated = entries.map((e, i) =>
        i === index ? { ...e, [field]: val } : e
      )
      onChange({ ...rule, sourceEntries: updated })
    },
    [rule, entries, onChange]
  )

  return (
    <>
      <div className={styles.columnCardField}>
        <div className={styles.mappingHeader}>
          <span className={styles.columnCardFieldLabel}>SOURCE ENTRIES</span>
          {!readOnly && (
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddEntry}
            >
              Add
            </Button>
          )}
        </div>
        {entries.map((entry, i) => (
          <div key={i} className={styles.mappingRow}>
            <Select
              size="small"
              value={entry.source || undefined}
              onChange={(val: string) => handleEntryChange(i, 'source', val)}
              style={{ flex: 1, minWidth: 0 }}
              placeholder="Source column"
              disabled={readOnly}
              popupMatchSelectWidth={false}
              {...searchableSelectProps}
              options={buildSourceOptions(sourceColumns, computedFieldNames)}
            />
            <span className={styles.mappingArrow}>→</span>
            <Input
              size="small"
              placeholder="Mapped value"
              value={entry.value}
              disabled={readOnly}
              onChange={(e) => handleEntryChange(i, 'value', e.target.value)}
              style={{ flex: 1 }}
            />
            {!readOnly && (
              <DeleteOutlined
                className={styles.mappingRowDelete}
                onClick={() => handleRemoveEntry(i)}
              />
            )}
          </div>
        ))}
      </div>

      <div className={styles.columnCardField}>
        <span className={styles.columnCardFieldLabel}>DEFAULT VALUE</span>
        <Input
          size="small"
          placeholder="Fallback when no source has data"
          value={rule.default ?? ''}
          disabled={readOnly}
          onChange={(e) => onChange({ ...rule, default: e.target.value })}
        />
      </div>
    </>
  )
})

const ContainsMapEditor = memo(function ContainsMapEditor({
  rule,
  sourceColumns,
  computedFieldNames,
  sourceValuesByColumn,
  onChange,
  readOnly,
}: {
  rule: MappingRule
  sourceColumns: string[]
  computedFieldNames: string[]
  sourceValuesByColumn?: Record<string, string[]>
  onChange: (updated: MappingRule) => void
  readOnly?: boolean
}) {
  const sourceValueOptions = useMemo(() => {
    if (!sourceValuesByColumn || !rule.source) return []
    const values = sourceValuesByColumn[rule.source] ?? []
    return [...values]
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ label: v, value: v }))
  }, [sourceValuesByColumn, rule.source])

  const entriesRef = useRef<MappingEntry[]>(mappingToEntries(rule.mapping))
  const lastSyncedMappingRef = useRef(rule.mapping)

  if (rule.mapping !== lastSyncedMappingRef.current) {
    const incomingJson = JSON.stringify(rule.mapping ?? {})
    const currentJson = JSON.stringify(entriesToMapping(entriesRef.current))
    if (incomingJson !== currentJson) {
      entriesRef.current = mappingToEntries(rule.mapping)
    }
    lastSyncedMappingRef.current = rule.mapping
  }

  const syncEntriesToRule = useCallback(
    (entries: MappingEntry[]) => {
      entriesRef.current = entries
      const newMapping = entriesToMapping(entries)
      lastSyncedMappingRef.current = newMapping
      onChange({ ...rule, mapping: newMapping })
    },
    [rule, onChange]
  )

  const handleAddEntry = useCallback(() => {
    const newEntries = [
      ...entriesRef.current,
      { _id: nextEntryId(), key: '', value: '' },
    ]
    syncEntriesToRule(newEntries)
  }, [syncEntriesToRule])

  const handleRemoveEntry = useCallback(
    (id: string) => {
      const newEntries = entriesRef.current.filter((e) => e._id !== id)
      syncEntriesToRule(newEntries)
    },
    [syncEntriesToRule]
  )

  const handleEntryKeyChange = useCallback(
    (id: string, newKey: string) => {
      const newEntries = entriesRef.current.map((e) =>
        e._id === id ? { ...e, key: newKey } : e
      )
      syncEntriesToRule(newEntries)
    },
    [syncEntriesToRule]
  )

  const handleEntryValueChange = useCallback(
    (id: string, newValue: string) => {
      const newEntries = entriesRef.current.map((e) =>
        e._id === id ? { ...e, value: newValue } : e
      )
      syncEntriesToRule(newEntries)
    },
    [syncEntriesToRule]
  )

  return (
    <>
      <div className={styles.columnCardField}>
        <span className={styles.columnCardFieldLabel}>SOURCE</span>
        <Select
          size="small"
          value={rule.source ?? undefined}
          onChange={(val: string) => onChange({ ...rule, source: val })}
          style={{ width: '100%' }}
          placeholder="Select source column"
          disabled={readOnly}
          popupMatchSelectWidth={false}
          {...searchableSelectProps}
          options={buildSourceOptions(sourceColumns, computedFieldNames)}
        />
      </div>

      <div className={styles.columnCardField}>
        <div className={styles.mappingHeader}>
          <span className={styles.columnCardFieldLabel}>
            CONTAINS MAPPING TABLE
          </span>
          {!readOnly && (
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddEntry}
            >
              Add
            </Button>
          )}
        </div>
        {entriesRef.current.map((entry) => (
          <div key={entry._id} className={styles.mappingRow}>
            <AutoComplete
              size="small"
              placeholder="Contains pattern"
              value={entry.key}
              disabled={readOnly}
              onChange={(val: string) => handleEntryKeyChange(entry._id, val)}
              options={sourceValueOptions}
              style={{ flex: 1 }}
              filterOption={(input, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
            <span className={styles.mappingArrow}>→</span>
            <Input
              size="small"
              placeholder="Mapped value"
              value={entry.value}
              disabled={readOnly}
              onChange={(e) =>
                handleEntryValueChange(entry._id, e.target.value)
              }
              style={{ flex: 1 }}
            />
            {!readOnly && (
              <DeleteOutlined
                className={styles.mappingRowDelete}
                onClick={() => handleRemoveEntry(entry._id)}
              />
            )}
          </div>
        ))}
      </div>

      <div className={styles.columnCardField}>
        <span className={styles.columnCardFieldLabel}>DEFAULT VALUE</span>
        <Input
          size="small"
          placeholder="Fallback when no pattern matches"
          value={rule.default ?? ''}
          disabled={readOnly}
          onChange={(e) => onChange({ ...rule, default: e.target.value })}
        />
      </div>

      <div className={styles.columnCardField}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
          }}
        >
          <input
            type="checkbox"
            checked={rule.case_insensitive !== false}
            disabled={readOnly}
            onChange={(e) =>
              onChange({ ...rule, case_insensitive: e.target.checked })
            }
          />
          Case insensitive
        </label>
      </div>
    </>
  )
})

const ConditionalNumericEditor = memo(function ConditionalNumericEditor({
  rule,
  sourceColumns,
  computedFieldNames,
  onChange,
  readOnly,
}: {
  rule: MappingRule
  sourceColumns: string[]
  computedFieldNames: string[]
  onChange: (updated: MappingRule) => void
  readOnly?: boolean
}) {
  const rules = rule.rules ?? []

  const operatorOptions = useMemo(
    () =>
      ALL_NUMERIC_COMPARISON_OPERATORS.map((operator) => ({
        label: `${NUMERIC_COMPARISON_OPERATOR_LABELS[operator]} (${NUMERIC_COMPARISON_OPERATOR_SYMBOLS[operator]})`,
        value: operator,
      })),
    []
  )

  const handleAddRule = useCallback(() => {
    onChange({
      ...rule,
      rules: [
        ...rules,
        {
          operator: NumericComparisonOperator.EQUAL,
          compareValue: '',
          outputValue: '',
        },
      ],
    })
  }, [rule, rules, onChange])

  const handleRemoveRule = useCallback(
    (index: number) => {
      if (rules.length <= 1) return
      onChange({
        ...rule,
        rules: rules.filter((_, i) => i !== index),
      })
    },
    [rule, rules, onChange]
  )

  const handleRuleChange = useCallback(
    (
      index: number,
      field: 'operator' | 'compareValue' | 'outputValue',
      value: string
    ) => {
      onChange({
        ...rule,
        rules: rules.map((condition, i) =>
          i === index ? { ...condition, [field]: value } : condition
        ),
      })
    },
    [rule, rules, onChange]
  )

  return (
    <>
      <div className={styles.columnCardField}>
        <span className={styles.columnCardFieldLabel}>SOURCE FIELD</span>
        <Select
          size="small"
          value={rule.sourceField || undefined}
          onChange={(val: string) => onChange({ ...rule, sourceField: val })}
          style={{ width: '100%' }}
          placeholder="Select numeric source column"
          disabled={readOnly}
          popupMatchSelectWidth={false}
          {...searchableSelectProps}
          options={buildSourceOptions(sourceColumns, computedFieldNames)}
        />
      </div>

      <div className={styles.columnCardField}>
        <div className={styles.mappingHeader}>
          <span className={styles.columnCardFieldLabel}>COMPARISON RULES</span>
          {!readOnly && (
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddRule}
            >
              Add rule
            </Button>
          )}
        </div>
        {rules.length > 1 && (
          <div
            className={styles.mappingMultiHint}
            data-testid="conditional-numeric-order-hint"
          >
            Rules are evaluated top to bottom. If conditions overlap, the first
            matching output is used.
          </div>
        )}
        {rules.map((condition, i) => {
          const compareValueText = String(condition.compareValue ?? '')
          const isCompareValueInvalid =
            compareValueText.trim() !== '' &&
            parseNumericInput(compareValueText) === null
          return (
            <div
              key={i}
              className={styles.mappingMultiRow}
              data-testid="conditional-numeric-rule-row"
            >
              <Select
                size="small"
                value={condition.operator}
                onChange={(val: NumericComparisonOperator) =>
                  handleRuleChange(i, 'operator', val)
                }
                style={{ flex: 1, minWidth: 0 }}
                placeholder="Operator"
                disabled={readOnly}
                popupMatchSelectWidth={false}
                {...searchableSelectProps}
                options={operatorOptions}
                data-testid="conditional-numeric-operator"
              />
              <Input
                size="small"
                placeholder="Compare value"
                value={compareValueText}
                disabled={readOnly}
                onChange={(e) =>
                  handleRuleChange(i, 'compareValue', e.target.value)
                }
                style={{
                  flex: 1,
                  minWidth: 0,
                  borderColor: isCompareValueInvalid ? '#e53e3e' : undefined,
                }}
                data-testid="conditional-numeric-compare-value"
              />
              <span className={styles.mappingArrow}>→</span>
              <Input
                size="small"
                placeholder="Output value"
                value={condition.outputValue ?? ''}
                disabled={readOnly}
                onChange={(e) =>
                  handleRuleChange(i, 'outputValue', e.target.value)
                }
                style={{ flex: 1, minWidth: 0 }}
                data-testid="conditional-numeric-output-value"
              />
              {!readOnly && (
                <DeleteOutlined
                  className={styles.mappingRowDelete}
                  onClick={() => handleRemoveRule(i)}
                  data-testid="conditional-numeric-rule-delete"
                  style={{
                    opacity: rules.length <= 1 ? 0.35 : 1,
                    cursor: rules.length <= 1 ? 'not-allowed' : 'pointer',
                  }}
                />
              )}
            </div>
          )
        })}
      </div>
    </>
  )
})

const SortableConcatSourceRow = memo(function SortableConcatSourceRow({
  id,
  index,
  value,
  sourceColumns,
  computedFieldNames,
  canRemove,
  readOnly,
  onChangeValue,
  onRemove,
}: {
  id: string
  index: number
  value: string
  sourceColumns: string[]
  computedFieldNames: string[]
  canRemove: boolean
  readOnly?: boolean
  onChangeValue: (index: number, val: string) => void
  onRemove: (index: number) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style: React.CSSProperties = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    opacity: isDragging ? 0.85 : undefined,
    zIndex: isDragging ? 2 : undefined,
    position: 'relative',
  }

  return (
    <div ref={setNodeRef} style={style} className={styles.mappingRow}>
      {!readOnly && (
        <button
          type="button"
          className={styles.dragHandle}
          aria-label={`Drag to reorder source ${index + 1}`}
          {...attributes}
          {...listeners}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <DragOutlined />
        </button>
      )}
      <Tag color="#003146" style={{ margin: 0, fontSize: 11, minWidth: 22 }}>
        {index + 1}
      </Tag>
      <Select
        size="small"
        value={value || undefined}
        onChange={(val: string) => onChangeValue(index, val)}
        style={{ flex: 1, minWidth: 0 }}
        placeholder={`Source ${index + 1}`}
        disabled={readOnly}
        popupMatchSelectWidth={false}
        {...searchableSelectProps}
        options={buildSourceOptions(sourceColumns, computedFieldNames)}
      />
      {!readOnly && (
        <DeleteOutlined
          className={styles.mappingRowDelete}
          onClick={() => (canRemove ? onRemove(index) : undefined)}
          style={{
            opacity: canRemove ? 1 : 0.35,
            cursor: canRemove ? 'pointer' : 'not-allowed',
          }}
          aria-disabled={!canRemove}
        />
      )}
    </div>
  )
})

const ConcatEditor = memo(function ConcatEditor({
  rule,
  sourceColumns,
  computedFieldNames,
  sourceValuesByColumn,
  onChange,
  readOnly,
}: {
  rule: MappingRule
  sourceColumns: string[]
  computedFieldNames: string[]
  sourceValuesByColumn?: Record<string, string[]>
  onChange: (updated: MappingRule) => void
  readOnly?: boolean
}) {
  const sources = useMemo(() => rule.sources ?? [], [rule.sources])
  const delimiter = rule.delimiter ?? ''

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  )

  const sortableIds = useMemo(
    () => sources.map((_, i) => `concat-source-${i}`),
    [sources]
  )

  const atMax = sources.length >= CONCAT_MAX_SOURCES
  const canRemove = sources.length > CONCAT_MIN_SOURCES

  const handleAddSource = useCallback(() => {
    if (sources.length >= CONCAT_MAX_SOURCES) return
    onChange({ ...rule, sources: [...sources, ''] })
  }, [rule, sources, onChange])

  const handleRemoveSource = useCallback(
    (index: number) => {
      onChange({ ...rule, sources: sources.filter((_, i) => i !== index) })
    },
    [rule, sources, onChange]
  )

  const handleSourceChange = useCallback(
    (index: number, val: string) => {
      onChange({
        ...rule,
        sources: sources.map((s, i) => (i === index ? val : s)),
      })
    },
    [rule, sources, onChange]
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = sortableIds.indexOf(String(active.id))
      const newIndex = sortableIds.indexOf(String(over.id))
      if (oldIndex < 0 || newIndex < 0) return
      onChange({ ...rule, sources: arrayMove(sources, oldIndex, newIndex) })
    },
    [rule, sources, sortableIds, onChange]
  )

  const handleDelimiterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...rule, delimiter: e.target.value })
    },
    [rule, onChange]
  )

  // Reuse the ported backend validator so the inline error is identical to the
  // save-time / backend error (only the leading prefix differs).
  const validationError = useMemo(() => {
    const concatRule: ConcatRule = {
      target: rule.target,
      condition: ConditionType.CONCAT,
      sources,
      delimiter,
    }
    return validateConcatRule(concatRule, 'This CONCAT rule')
  }, [rule.target, sources, delimiter])

  // Live preview using a representative row (first known value per source
  // column). Mirrors the backend transform exactly via evaluateConcat.
  const preview = useMemo(() => {
    if (
      sources.length < CONCAT_MIN_SOURCES ||
      sources.length > CONCAT_MAX_SOURCES
    ) {
      return null
    }
    if (sources.some((s) => s.trim() === '')) return null
    const sampleRow: Record<string, string> = {}
    for (const s of sources) {
      const key = s.trim()
      sampleRow[key] = sourceValuesByColumn?.[key]?.[0] ?? ''
    }
    const concatRule: ConcatRule = {
      target: rule.target,
      condition: ConditionType.CONCAT,
      sources,
      delimiter,
    }
    return evaluateConcat(concatRule, sampleRow)
  }, [sources, delimiter, rule.target, sourceValuesByColumn])

  return (
    <>
      <div className={styles.columnCardField}>
        <div className={styles.mappingHeader}>
          <span className={styles.columnCardFieldLabel}>
            SOURCE FIELDS ({sources.length}/{CONCAT_MAX_SOURCES})
          </span>
          {!readOnly && (
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddSource}
              disabled={atMax}
              title={
                atMax
                  ? `A CONCAT rule allows at most ${CONCAT_MAX_SOURCES} source fields`
                  : undefined
              }
            >
              Add field
            </Button>
          )}
        </div>

        <div className={styles.mappingMultiHint}>
          Sources are joined <strong>in order</strong>. Drag the handle to
          reorder. At runtime every source must have a value or the whole record
          is skipped and an error is written to the <code>Errors</code> column.
        </div>

        {readOnly ? (
          sources.map((value, i) => (
            <SortableConcatSourceRow
              key={`concat-source-${i}`}
              id={`concat-source-${i}`}
              index={i}
              value={value}
              sourceColumns={sourceColumns}
              computedFieldNames={computedFieldNames}
              canRemove={false}
              readOnly
              onChangeValue={handleSourceChange}
              onRemove={handleRemoveSource}
            />
          ))
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableIds}
              strategy={verticalListSortingStrategy}
            >
              {sources.map((value, i) => (
                <SortableConcatSourceRow
                  key={`concat-source-${i}`}
                  id={`concat-source-${i}`}
                  index={i}
                  value={value}
                  sourceColumns={sourceColumns}
                  computedFieldNames={computedFieldNames}
                  canRemove={canRemove}
                  onChangeValue={handleSourceChange}
                  onRemove={handleRemoveSource}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}

        {validationError && (
          <div className={styles.concatError} role="alert">
            {validationError}
          </div>
        )}
      </div>

      <div className={styles.columnCardField}>
        <span className={styles.columnCardFieldLabel}>DELIMITER</span>
        <Input
          size="small"
          value={delimiter}
          onChange={handleDelimiterChange}
          placeholder='e.g. "-", "/", "_" (leave empty to join directly)'
          disabled={readOnly}
        />
      </div>

      {preview && (
        <div className={styles.columnCardField}>
          <span className={styles.columnCardFieldLabel}>PREVIEW</span>
          {preview.error ? (
            <div
              className={`${styles.concatPreview} ${styles.concatPreviewError}`}
            >
              {preview.error}
            </div>
          ) : (
            <div className={styles.concatPreview}>
              <code>{preview.value === '' ? '(empty)' : preview.value}</code>
            </div>
          )}
        </div>
      )}
    </>
  )
})

export default memo(function ColumnCard({
  rule,
  index,
  sourceColumns,
  computedFieldNames,
  sourceValuesByColumn,
  onChange,
  onRemove,
  dragHandleProps,
  isMoved,
  readOnly,
}: ColumnCardProps) {
  const handleTargetChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...rule, target: e.target.value })
    },
    [rule, onChange]
  )

  const handleConditionChange = useCallback(
    (condition: ConditionType) => {
      const updated: MappingRule = {
        ...rule,
        condition,
        value: undefined,
        source: undefined,
        sources: undefined,
        sourceEntries: undefined,
        mapping: undefined,
        default: undefined,
        normalize: undefined,
        constant: undefined,
        sourceField: undefined,
        rules: undefined,
      }

      switch (condition) {
        case ConditionType.HARDCODE:
          updated.value = ''
          break
        case ConditionType.DIRECT_MAPPING:
          updated.source = ''
          updated.normalize = []
          break
        case ConditionType.GET_MAX_DATE:
          updated.sources = []
          break
        case ConditionType.LOOKUP_MAP:
          updated.source = ''
          updated.mapping = {}
          updated.default = ''
          break
        case ConditionType.FIRST_AVAILABLE:
          updated.sources = []
          updated.normalize = []
          break
        case ConditionType.FIRST_AVAILABLE_MAP:
          updated.sourceEntries = []
          updated.default = ''
          break
        case ConditionType.CONTAINS_MAP:
          updated.source = ''
          updated.mapping = {}
          updated.default = ''
          updated.case_insensitive = true
          break
        case ConditionType.MULTIPLY:
          updated.source = ''
          updated.constant = 1
          break
        case ConditionType.CONDITIONAL_NUMERIC:
          updated.sourceField = ''
          updated.rules = [
            {
              operator: NumericComparisonOperator.EQUAL,
              compareValue: '',
              outputValue: '',
            },
          ]
          break
        case ConditionType.CONCAT:
          // Seed with the minimum number of (empty) source slots so the editor
          // renders the required rows immediately. Delimiter defaults to "-".
          updated.sources = Array.from({ length: CONCAT_MIN_SOURCES }, () => '')
          updated.delimiter = '-'
          break
      }

      onChange(updated)
    },
    [rule, onChange]
  )

  const handleNormalizeChange = useCallback(
    (normalize: NormalizeStep[]) => {
      onChange({ ...rule, normalize })
    },
    [rule, onChange]
  )

  const showNormalization =
    rule.condition === ConditionType.DIRECT_MAPPING ||
    rule.condition === ConditionType.FIRST_AVAILABLE

  return (
    <div
      className={`${styles.columnCard} ${
        isMoved ? styles.columnCardMoved : ''
      }`}
    >
      <div className={styles.columnCardToolbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tag
            color="#003146"
            style={{ margin: 0, fontSize: 11, fontWeight: 700 }}
          >
            #{index + 1}
          </Tag>
          {!readOnly && dragHandleProps && (
            <button
              type="button"
              className={styles.dragHandle}
              aria-label="Drag to reorder target fields"
              {...dragHandleProps}
            >
              <DragOutlined />
            </button>
          )}
        </div>
        {!readOnly && (
          <CloseOutlined
            className={styles.columnCardClose}
            onClick={() => onRemove(rule.id)}
          />
        )}
      </div>

      <div className={styles.columnCardField}>
        <span className={styles.columnCardFieldLabel}>TARGET FIELD</span>
        <Input
          size="small"
          value={rule.target}
          onChange={handleTargetChange}
          placeholder="Target field name"
          disabled={readOnly}
        />
      </div>

      <div className={styles.columnCardField}>
        <span className={styles.columnCardFieldLabel}>CONDITION TYPE</span>
        <Select
          size="small"
          value={rule.condition}
          onChange={handleConditionChange}
          style={{ width: '100%' }}
          disabled={readOnly}
          {...searchableSelectProps}
          options={ALL_CONDITION_TYPES.map((ct) => ({
            label: CONDITION_LABELS[ct],
            value: ct,
          }))}
        />
      </div>

      {/* Hardcode */}
      {rule.condition === ConditionType.HARDCODE && (
        <div className={styles.columnCardField}>
          <span className={styles.columnCardFieldLabel}>VALUE</span>
          <Input
            size="small"
            value={rule.value ?? ''}
            onChange={(e) => onChange({ ...rule, value: e.target.value })}
            placeholder="Fixed value"
            disabled={readOnly}
          />
        </div>
      )}

      {/* Direct Mapping */}
      {rule.condition === ConditionType.DIRECT_MAPPING && (
        <div className={styles.columnCardField}>
          <span className={styles.columnCardFieldLabel}>SOURCE</span>
          <Select
            size="small"
            value={rule.source || undefined}
            onChange={(val: string) => onChange({ ...rule, source: val })}
            style={{ width: '100%' }}
            placeholder="Select source column"
            disabled={readOnly}
            popupMatchSelectWidth={false}
            {...searchableSelectProps}
            options={buildSourceOptions(sourceColumns, computedFieldNames)}
          />
        </div>
      )}

      {/* GetMaxDate */}
      {rule.condition === ConditionType.GET_MAX_DATE && (
        <div className={styles.columnCardField}>
          <span className={styles.columnCardFieldLabel}>
            DATE SOURCE COLUMNS
          </span>
          <Select
            mode="multiple"
            size="small"
            value={rule.sources ?? []}
            onChange={(val: string[]) => onChange({ ...rule, sources: val })}
            style={{ width: '100%' }}
            placeholder="Select date columns"
            disabled={readOnly}
            popupMatchSelectWidth={false}
            {...searchableSelectProps}
            options={buildSourceOptions(sourceColumns, computedFieldNames)}
          />
        </div>
      )}

      {/* Lookup Map */}
      {rule.condition === ConditionType.LOOKUP_MAP && (
        <LookupMapEditor
          rule={rule}
          sourceColumns={sourceColumns}
          computedFieldNames={computedFieldNames}
          sourceValuesByColumn={sourceValuesByColumn}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}

      {/* First Available */}
      {rule.condition === ConditionType.FIRST_AVAILABLE && (
        <div className={styles.columnCardField}>
          <span className={styles.columnCardFieldLabel}>SOURCE COLUMNS</span>
          <Select
            mode="multiple"
            size="small"
            value={rule.sources ?? []}
            onChange={(val: string[]) => onChange({ ...rule, sources: val })}
            style={{ width: '100%' }}
            placeholder="Select sources (first non-empty wins)"
            disabled={readOnly}
            popupMatchSelectWidth={false}
            {...searchableSelectProps}
            options={buildSourceOptions(sourceColumns, computedFieldNames)}
          />
        </div>
      )}

      {/* First Available Map */}
      {rule.condition === ConditionType.FIRST_AVAILABLE_MAP && (
        <FirstAvailableMapEditor
          rule={rule}
          sourceColumns={sourceColumns}
          computedFieldNames={computedFieldNames}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}

      {/* Contains Map */}
      {rule.condition === ConditionType.CONTAINS_MAP && (
        <ContainsMapEditor
          rule={rule}
          sourceColumns={sourceColumns}
          computedFieldNames={computedFieldNames}
          sourceValuesByColumn={sourceValuesByColumn}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}

      {/* Multiply */}
      {rule.condition === ConditionType.MULTIPLY && (
        <>
          <div className={styles.columnCardField}>
            <span className={styles.columnCardFieldLabel}>SOURCE</span>
            <Select
              size="small"
              value={rule.source || undefined}
              onChange={(val: string) => onChange({ ...rule, source: val })}
              style={{ width: '100%' }}
              placeholder="Select numeric source column"
              disabled={readOnly}
              popupMatchSelectWidth={false}
              {...searchableSelectProps}
              options={buildSourceOptions(sourceColumns, computedFieldNames)}
            />
          </div>
          <div className={styles.columnCardField}>
            <span className={styles.columnCardFieldLabel}>CONSTANT</span>
            <Input
              size="small"
              type="number"
              placeholder="e.g. 2080"
              value={rule.constant ?? ''}
              disabled={readOnly}
              onChange={(e) => {
                const raw = e.target.value
                onChange({
                  ...rule,
                  constant: raw === '' ? undefined : Number(raw),
                })
              }}
              onBlur={() => {
                // Restore the multiplicative identity if the field was left
                // blank or NaN; multiplying by an undefined/invalid constant
                // would otherwise produce empty target values at preview time.
                if (
                  typeof rule.constant !== 'number' ||
                  !Number.isFinite(rule.constant)
                ) {
                  onChange({ ...rule, constant: 1 })
                }
              }}
            />
          </div>
        </>
      )}

      {/* Conditional Numeric */}
      {rule.condition === ConditionType.CONDITIONAL_NUMERIC && (
        <ConditionalNumericEditor
          rule={rule}
          sourceColumns={sourceColumns}
          computedFieldNames={computedFieldNames}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}

      {/* Concat / Text Join */}
      {rule.condition === ConditionType.CONCAT && (
        <ConcatEditor
          rule={rule}
          sourceColumns={sourceColumns}
          computedFieldNames={computedFieldNames}
          sourceValuesByColumn={sourceValuesByColumn}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}

      {/* Normalization */}
      {showNormalization && (
        <NormalizationEditor
          steps={rule.normalize ?? []}
          onChange={handleNormalizeChange}
          readOnly={readOnly}
        />
      )}
    </div>
  )
})
